"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight, Newspaper } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { getLenisInstance, isScrolling } from "@/lib/lenis-instance";
import { PageHero } from "@/components/sections/page-hero";
import { CssStarfield } from "@/components/sections/css-starfield";
import { SceneAtmosphere } from "@/components/scroll/scene-atmosphere";
import { Container } from "@/components/layout/container";
import { KineticHeadline } from "@/components/ui/kinetic-headline";
import { JourneyCta } from "@/components/sections/journey/journey-cta";
import { BlogHeroIllustration } from "@/components/illustrations/blog-hero";
import type { BlogCard } from "@/lib/blog";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

function CardImage({ src }: { src: string }) {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <>
      {!loaded && <span className="img-skeleton" aria-hidden />}
      <Image
        ref={ref}
        src={src}
        alt=""
        fill
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        className="object-cover transition-opacity duration-300 ease-out"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </>
  );
}

export function BlogIndex({ posts }: { posts: BlogCard[] }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reveal = gsap.utils.toArray<HTMLElement>("[data-reveal]", root);
    if (reveal.length === 0) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(reveal, { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      reveal.forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 28 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            // The animated y-offset must not change the page height ScrollTrigger measures, or Lenis
            // caches a height that's off by the offset and wheel scroll clamps short of the footer.
            invalidateOnRefresh: true,
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }, root);

    // Lenis caches the scrollable limit at init, before the cards' cover images load and grow the
    // page. Without re-measuring, wheel/trackpad scroll clamps ~60px short of the footer (native
    // scrollbar drag still reaches it, which is the tell). Re-measure both engines after mount, and
    // again once each cover image finishes loading, since each one changes the page height.
    // Debounce so a burst of cover-image loads coalesces into ONE refresh. Repeatedly calling
    // ScrollTrigger.refresh() mid-scroll snaps the Lenis scroll position and can lock scrolling
    // until the page is reloaded.
    let remeasureTimeout: ReturnType<typeof setTimeout>;
    function remeasure() {
      clearTimeout(remeasureTimeout);
      remeasureTimeout = setTimeout(function run() {
        if (isScrolling()) {
          remeasureTimeout = setTimeout(run, 150);
          return;
        }
        getLenisInstance()?.resize();
        ScrollTrigger.refresh();
      }, 200);
    }
    const t = setTimeout(remeasure, 250);

    const imgs = Array.from(root.querySelectorAll("img"));
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", remeasure, { once: true });
    });

    return () => {
      clearTimeout(t);
      clearTimeout(remeasureTimeout);
      imgs.forEach((img) => img.removeEventListener("load", remeasure));
      ctx.revert();
    };
    // Keyed on the count, not `posts` itself: the array is a fresh reference every render and would
    // tear down and rebuild every ScrollTrigger on each one.
  }, [posts.length]);

  return (
    <div ref={rootRef}>
      {/* No bg on the wrapper: the starfield sits at -z-10, so an opaque ancestor background paints
          over it and the hero reads as flat black. The page background comes from <body>, exactly
          as it does on the other hero pages. Seed 37 keeps the field dense in the hero band. */}
      <div className="relative overflow-hidden">
        <CssStarfield seed={37} className="absolute inset-0 -z-10" />
        <SceneAtmosphere x="18%" y="30%" intensity={0.14} size={66} />
        <PageHero
          id="blog-heading"
          eyebrow="Blog"
          lines={["How software", "actually ships."]}
          description="Field notes on building AI into real products — RAG, chatbots, agents, and the architecture decisions that outlive a release. Written by the team that ships them."
          accentColor="#a374e8"
          seed={5}
          illustration={BlogHeroIllustration}
          illustrationLabel="Illustration of a person writing a blog post at a desk"
          headlineVariant="word-flip"
        />
      </div>

      <section
        aria-labelledby="posts-heading"
        className="relative overflow-hidden px-6 py-12 sm:py-[9vh] lg:px-10"
      >
        <SceneAtmosphere x="80%" y="30%" intensity={0.12} size={68} />
        <Container className="relative z-10">
          <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)]">
            Latest
          </span>
          <KineticHeadline
            as="h2"
            id="posts-heading"
            variant="slide-up"
            lines={["Notes from the", "delivery floor."]}
            className="mt-4 max-w-3xl font-display text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.05] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />
          <div data-reveal className="mt-8 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <p className="text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]">
              Retrieval pipelines that return the wrong passage with total confidence. Chatbots that
              pass a demo and fail a customer. The moment a model stops answering and starts acting,
              and what that changes about who can approve what. These are the problems that show up
              once an AI feature meets production, and the tradeoffs behind each one.
            </p>
            <p className="text-base leading-relaxed text-pretty text-[var(--color-text-tertiary)]">
              Alongside them: the engineering decisions that outlive a release — how to version an
              API before anyone depends on it, which technical debt compounds and which is safe to
              carry, and when blockchain is genuinely the right tool rather than a database with
              extra steps.
            </p>
          </div>

          <dl className="mt-14 grid grid-cols-1 gap-8 border-y border-[var(--color-border)] py-10 sm:grid-cols-3">
            {[
              {
                term: "AI in production",
                def: "RAG, retrieval quality, hallucination, agents, and prompt injection — what holds up once real users and real documents are involved.",
              },
              {
                term: "Architecture calls",
                def: "The decisions with a long tail: model selection, API contracts, cross-platform mobile, and the debt worth paying down.",
              },
              {
                term: "Industry context",
                def: "How the same technology lands differently in healthcare, fintech, EdTech, and e-commerce — where the constraints actually bind.",
              },
            ].map((item) => (
              <div key={item.term} data-reveal>
                <dt className="font-display text-lg font-semibold text-[var(--color-primary-text)]">
                  {item.term}
                </dt>
                <dd className="mt-2 text-sm leading-relaxed text-pretty text-[var(--color-text-secondary)]">
                  {item.def}
                </dd>
              </div>
            ))}
          </dl>

          {posts.length === 0 ? (
            <div
              data-reveal
              className="mt-14 flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-6 py-20 text-center"
            >
              <div className="flex size-14 items-center justify-center rounded-2xl bg-[radial-gradient(circle_at_30%_30%,color-mix(in_oklch,var(--color-primary)_22%,transparent),transparent_70%)]">
                <Newspaper aria-hidden className="size-7 text-[var(--color-primary-text)]/60" />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-[var(--color-text-primary)]">
                No posts yet
              </h3>
              <p className="mt-2 max-w-md text-[0.9375rem] leading-relaxed text-pretty text-[var(--color-text-secondary)]">
                New posts are on the way. In the meantime, explore the work or start a
                conversation about your project.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  href="/portfolio"
                  className="rounded-full border border-[var(--color-border)] px-5 py-2 font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-primary)]/40 hover:text-[var(--color-primary-text)]"
                >
                  See the work
                </Link>
                <Link
                  href="/contact"
                  className="rounded-full bg-[var(--color-primary)] px-5 py-2 font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-white transition-colors hover:bg-[var(--color-primary-dark)]"
                >
                  Start a project
                </Link>
              </div>
            </div>
          ) : (
          <ul className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <li key={post.slug} data-reveal>
                <Link
                  href={`/blog/${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] transition-colors hover:border-[var(--color-primary)]/40"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[var(--color-bg-surface)]">
                    {post.image ? (
                      <CardImage src={post.image} />
                    ) : (
                      // A post without a cover image gets a branded panel so the grid stays uniform.
                      <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_30%,color-mix(in_oklch,var(--color-primary)_22%,transparent),transparent_70%)]">
                        <Newspaper aria-hidden className="size-9 text-[var(--color-primary-text)]/50" />
                      </div>
                    )}
                    <span className="absolute left-3 top-3 rounded-full bg-[var(--color-bg)]/80 px-2.5 py-1 font-mono-tech text-[0.65rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)] backdrop-blur-sm">
                      {post.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-bold leading-snug text-pretty text-[var(--color-text-primary)] transition-colors group-hover:text-[var(--color-primary-text)]">
                      {post.title}
                    </h3>
                    <p className="mt-3 flex-1 text-[0.9375rem] leading-relaxed text-pretty text-[var(--color-text-secondary)]">
                      {post.summary}
                    </p>
                    <span className="mt-5 flex items-center gap-2 font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-text-tertiary)]">
                      {post.readMinutes} min read
                      <ArrowUpRight
                        aria-hidden
                        className="size-4 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transition-none"
                      />
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          )}
        </Container>
      </section>

      <JourneyCta
        heading="Let's Start a Conversation"
        body="Reading is one thing. Tell us what you're building and we'll come back within one business day with a scoped plan."
        primaryLabel="Start a project"
        secondaryHref="/portfolio"
        secondaryLabel="See the work"
      />
    </div>
  );
}
